/**
 * useAnalytics Hook
 * 
 * Self-hosted Analytics Client mit:
 * - Session Cookie (sg_session, TTL 7d, SameSite=Lax, Secure in prod)
 * - Event Queue (in-memory + optional localStorage)
 * - Flush via sendBeacon (fallback fetch)
 * - Dedupe für restaurant_profile_view (10min bucket)
 * - Consent respect (analytics_disabled localStorage)
 */

import { useEffect, useRef, useCallback } from 'react';

// Event Whitelist (P0 Events)
export type AnalyticsEventName =
  | 'restaurant_profile_view'
  | 'click_call'
  | 'click_route'
  | 'click_menu'
  | 'click_website'
  | 'listing_card_click'
  | 'search_submit'
  | 'filter_apply';

export interface AnalyticsEvent {
  event_name: AnalyticsEventName;
  ts?: string; // ISO string, optional (wird vom Client gesetzt)
  path: string;
  restaurant_id?: string | null;
  session_id: string;
  referrer?: string | null;
  metadata?: Record<string, any> | null;
}

const SESSION_COOKIE_NAME = 'sg_session';
const SESSION_TTL_DAYS = 7;
const PENDING_EVENTS_KEY = 'sg_pending_events';
const PENDING_EVENTS_MAX = 50;
const FLUSH_INTERVAL_MS = 10000; // 10 Sekunden
const FLUSH_QUEUE_SIZE = 10; // Flush wenn Queue >= 10 Events
const VIEW_DEDUPE_WINDOW_MS = 10 * 60 * 1000; // 10 Minuten
const VIEW_DEDUPE_KEY_PREFIX = 'pv:';
// Prod-Safety: einige AdBlocker blocken Pfade mit "analytics" → verwende unauffälligen Alias
const INGEST_PATH = '/api/_events';

// Session Cookie Management
function getSessionId(): string {
  // SSR/Test Guard
  if (typeof document === 'undefined') return `sg_ssr_${Date.now()}`;
  
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === SESSION_COOKIE_NAME && value) {
      return value;
    }
  }
  
  // Neue Session ID generieren
  const newSessionId = `sg_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  const expires = new Date();
  expires.setTime(expires.getTime() + SESSION_TTL_DAYS * 24 * 60 * 60 * 1000);
  
  const isSecure = window.location.protocol === 'https:';
  const cookieValue = `${SESSION_COOKIE_NAME}=${newSessionId}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${isSecure ? '; Secure' : ''}`;
  document.cookie = cookieValue;
  
  return newSessionId;
}

// Consent Check
function isAnalyticsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const disabled = localStorage.getItem('analytics_disabled');
  return disabled !== 'true';
}

// View Dedupe (10min bucket) - Reihenfolge: restaurantId:sessionId:bucket (matching server)
function getViewDedupeKey(restaurantId: string | null, sessionId: string, ts: Date): string {
  if (!restaurantId) return '';
  const bucket = Math.floor(ts.getTime() / VIEW_DEDUPE_WINDOW_MS);
  return `${VIEW_DEDUPE_KEY_PREFIX}${restaurantId}:${sessionId}:${bucket}`;
}

function isViewDeduplicated(restaurantId: string | null, sessionId: string): boolean {
  if (!restaurantId) return false;
  
  const now = new Date();
  const key = getViewDedupeKey(restaurantId, sessionId, now);
  const stored = localStorage.getItem(key);
  
  if (stored) {
    const storedTime = parseInt(stored, 10);
    const age = now.getTime() - storedTime;
    // Wenn jünger als 24h, ist dedupliziert
    if (age < 24 * 60 * 60 * 1000) {
      return true;
    }
  }
  
  // Speichere neuen Key mit Timestamp
  try {
    localStorage.setItem(key, now.getTime().toString());
  } catch {
    // localStorage voll — View trotzdem tracken (nicht dedupliziert)
    return false;
  }
  
  // Cleanup: Entferne alte Keys (>24h) - nur analytics prefix Keys iterieren
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(VIEW_DEDUPE_KEY_PREFIX)) {
        const val = localStorage.getItem(k);
        if (val) {
          const valTime = parseInt(val, 10);
          if (isNaN(valTime) || now.getTime() - valTime > 24 * 60 * 60 * 1000) {
            keysToRemove.push(k);
          }
        }
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch {
    // Ignoriere localStorage-Fehler bei Cleanup
  }
  
  return false;
}

// Pending Events (localStorage)
function loadPendingEvents(): AnalyticsEvent[] {
  try {
    const stored = localStorage.getItem(PENDING_EVENTS_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.slice(0, PENDING_EVENTS_MAX) : [];
  } catch {
    return [];
  }
}

function savePendingEvents(events: AnalyticsEvent[]): void {
  try {
    const toSave = events.slice(0, PENDING_EVENTS_MAX);
    localStorage.setItem(PENDING_EVENTS_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.warn('Fehler beim Speichern von pending events:', error);
  }
}

function clearPendingEvents(): void {
  try {
    localStorage.removeItem(PENDING_EVENTS_KEY);
  } catch {
    // Ignore
  }
}

// Flush Events zum Server
async function flushEvents(events: AnalyticsEvent[]): Promise<boolean> {
  if (events.length === 0) return true;
  
  if (!isAnalyticsEnabled()) {
    // Wenn Analytics disabled, lösche Events
    return true;
  }

  const payload = { events };
  // Wichtig: fetch (keepalive) statt sendBeacon, um Content-Type/Parser-Probleme zu vermeiden
  // und um nicht am "analytics" URL-Pattern von AdBlockern zu hängen.
  try {
    const response = await fetch(INGEST_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      keepalive: true, // Wichtig für pagehide
    });
    
    return response.ok;
  } catch (error) {
    console.warn('Fehler beim Senden von Analytics Events:', error);
    return false;
  }
}

// Analytics Client (Singleton)
class AnalyticsClient {
  private queue: AnalyticsEvent[] = [];
  private flushTimer: number | null = null;
  private sessionId: string;
  private handlePageHide: (() => void) | null = null;
  private handleVisibilityChange: (() => void) | null = null;
  private isFlushing = false; // Mutex: verhindert parallele flush-Aufrufe

  constructor() {
    this.sessionId = getSessionId();
    this.loadPendingEvents();
    this.setupFlushTimer();
    this.setupPageHideListener();
  }

  private loadPendingEvents(): void {
    const pending = loadPendingEvents();
    if (pending.length > 0) {
      this.queue.push(...pending);
      clearPendingEvents();
    }
  }

  private setupFlushTimer(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
    }
    
    this.flushTimer = window.setInterval(() => {
      this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  private setupPageHideListener(): void {
    this.handlePageHide = () => {
      this.flush();
    };
    
    this.handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    };
    
    window.addEventListener('pagehide', this.handlePageHide);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Räumt Timer und Event-Listener auf (für Shutdown/Tests)
   */
  dispose(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.handlePageHide) {
      window.removeEventListener('pagehide', this.handlePageHide);
      this.handlePageHide = null;
    }
    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.handleVisibilityChange = null;
    }
    // Flush remaining events to localStorage
    if (this.queue.length > 0) {
      savePendingEvents(this.queue.slice(0, PENDING_EVENTS_MAX));
    }
  }

  private async flush(): Promise<void> {
    // Mutex: verhindert dass Timer + pagehide gleichzeitig flushen
    // → Click-Events (kein Server-Dedupe) würden sonst doppelt gesendet
    if (this.isFlushing || this.queue.length === 0) return;
    
    this.isFlushing = true;
    try {
      const eventsToFlush = [...this.queue];
      this.queue = [];
      
      const success = await flushEvents(eventsToFlush);
      
      if (!success) {
        // Bei Fehler: zurück in Queue + localStorage
        this.queue.unshift(...eventsToFlush);
        savePendingEvents(this.queue.slice(0, PENDING_EVENTS_MAX));
        
        // Wenn Queue zu groß, entferne älteste
        if (this.queue.length > PENDING_EVENTS_MAX) {
          this.queue = this.queue.slice(-PENDING_EVENTS_MAX);
          savePendingEvents(this.queue);
        }
      } else {
        // Erfolg: lösche pending
        clearPendingEvents();
      }
    } finally {
      this.isFlushing = false;
    }
  }

  track(event: Omit<AnalyticsEvent, 'session_id' | 'ts' | 'path' | 'referrer'>): void {
    if (!isAnalyticsEnabled()) return;
    
    // Dedupe für restaurant_profile_view
    if (event.event_name === 'restaurant_profile_view' && event.restaurant_id) {
      if (isViewDeduplicated(event.restaurant_id, this.sessionId)) {
        return; // Skip duplicate
      }
    }
    
    const fullEvent: AnalyticsEvent = {
      ...event,
      session_id: this.sessionId,
      ts: new Date().toISOString(),
      path: window.location.pathname,
      referrer: document.referrer || null,
    };
    
    this.queue.push(fullEvent);
    
    // Dispatch custom event for affiliate tracking hook to intercept
    try {
      window.dispatchEvent(new CustomEvent('sg:analytics', {
        detail: {
          event_name: event.event_name,
          restaurant_id: event.restaurant_id || null,
          session_id: this.sessionId,
        },
      }));
    } catch {
      // Silently fail if CustomEvent not supported
    }
    
    // Flush wenn Queue groß genug
    if (this.queue.length >= FLUSH_QUEUE_SIZE) {
      this.flush();
    }
  }

  /**
   * trackAndFlush: Track event und flush SOFORT (für kritische Events vor Navigation)
   * Wird synchron ausgeführt, gibt Promise für Optional-Await zurück
   */
  async trackAndFlush(event: Omit<AnalyticsEvent, 'session_id' | 'ts' | 'path' | 'referrer'>): Promise<void> {
    if (!isAnalyticsEnabled()) return;
    
    const fullEvent: AnalyticsEvent = {
      ...event,
      session_id: this.sessionId,
      ts: new Date().toISOString(),
      path: window.location.pathname,
      referrer: document.referrer || null,
    };
    
    // Direkt senden ohne Queue (für tel:/external links die keine pagehide triggern)
    await flushEvents([fullEvent]);
  }

  getSessionId(): string {
    return this.sessionId;
  }
}

// Singleton Instance
let analyticsClient: AnalyticsClient | null = null;

function getAnalyticsClient(): AnalyticsClient {
  if (!analyticsClient) {
    analyticsClient = new AnalyticsClient();
  }
  return analyticsClient;
}

/**
 * useAnalytics Hook
 * 
 * @returns { track, getSessionId }
 */
export function useAnalytics() {
  const clientRef = useRef<AnalyticsClient | null>(null);
  
  useEffect(() => {
    if (!clientRef.current) {
      clientRef.current = getAnalyticsClient();
    }
    
    return () => {
      // Cleanup: Flush bei Unmount
      if (clientRef.current) {
        // Flush wird asynchron ausgeführt, kein await hier
        clientRef.current['flush']();
      }
    };
  }, []);
  
  const track = useCallback((event: Omit<AnalyticsEvent, 'session_id' | 'ts' | 'path' | 'referrer'>) => {
    if (clientRef.current) {
      clientRef.current.track(event);
    }
  }, []);
  
  const trackAndFlush = useCallback(async (event: Omit<AnalyticsEvent, 'session_id' | 'ts' | 'path' | 'referrer'>) => {
    if (clientRef.current) {
      await clientRef.current.trackAndFlush(event);
    }
  }, []);
  
  const getSessionId = useCallback(() => {
    return clientRef.current?.getSessionId() || '';
  }, []);
  
  return { track, trackAndFlush, getSessionId };
}
